import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../../firebase/config";
import { collection, addDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { reverseGeocode } from "../../utils/geocode";
import { useLoadScript } from "@react-google-maps/api";
import { useLocation } from "react-router-dom";
import MapView from "./MapView";
import AddressAutocomplete from "./AddressAutocomplete";
import DayJobForm from "../Jobs/DayJobForm";
import { hasPremiumAccess } from "../../utils/access";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import { LOCATION_SOURCES } from "../../utils/locationUtils";
import {
  abandonAppFlow,
  completeAppFlow,
  startAppFlow,
} from "../../utils/appAnalytics";


const LIBRARIES = ["places", "geometry"];
const FIELD_JOB_TYPES = [
  { value: "grass_cutting", label: "Grass Cutting" },
  { value: "crop_planting", label: "Crop Planting" },
  { value: "crop_harvest", label: "Crop Harvest" },
  { value: "hay_cutting", label: "Hay Cutting" },
  { value: "baling", label: "Baling" },
  { value: "spraying", label: "Spraying" },
  { value: "tilling", label: "Tilling / Ground Prep" },
  { value: "brush_clearing", label: "Brush Clearing" },
  { value: "fencing", label: "Fencing" },
  { value: "custom", label: "Other / Custom" },
];

const LAND_USE_TYPES = [
  { value: "horse_grazing", label: "Horse grazing pasture" },
  { value: "livestock_grazing", label: "Livestock grazing" },
  { value: "fenced_dog_run", label: "Fenced dog run" },
  { value: "garden_plot", label: "Garden plot" },
  { value: "hay_field_access", label: "Hay field access" },
  { value: "equipment_parking", label: "Equipment parking" },
  { value: "temporary_holding", label: "Temporary holding area" },
  { value: "other", label: "Other land use" },
];

const MAP_MODES = [
  {
    value: "browse",
    icon: "Map",
    label: "Browse",
    helper: "Explore nearby jobs, fields, and land posts.",
  },
  {
    value: "field",
    icon: "Field",
    label: "Post Field Job",
    helper: "Draw the field boundary and add job details.",
  },
  {
    value: "day",
    icon: "Work",
    label: "Post Day Job",
    helper: "Choose a location and post local work.",
  },
  {
    value: "land_use",
    icon: "Land",
    label: "Land Share",
    helper: "Show available land or find space nearby.",
  },
];

const MAP_MODE_HELPERS = MAP_MODES.reduce((helpers, item) => {
  helpers[item.value] = item.helper;
  return helpers;
}, {});

const initialLandUseDetails = {
  title: "",
  description: "",
  landUseType: "horse_grazing",
  offeringOrSeeking: "offering",
  acreage: "",
  fenced: "unknown",
  waterAccess: "unknown",
  animalUseNotes: "",
  priceType: "contact",
  price: "",
};

const normalizeUnknownBoolean = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return "unknown";
};

const getFieldJobTypeLabel = (value, customValue = "") => {
  if (value === "custom") {
    return customValue.trim() || "Custom Field Job";
  }

  return (
    FIELD_JOB_TYPES.find((type) => type.value === value)?.label ||
    "Field Job"
  );
};

const FieldListingForm = ({ userProfile, userLocation: appUserLocation }) => {
  const premiumAllowed = hasPremiumAccess(userProfile);
  const [jobType, setJobType] = useState("grass_cutting");
const [customJobType, setCustomJobType] = useState("");
const [cropType, setCropType] = useState("");
  const [dealType, setDealType] = useState("per_acre");
  const [addressInput, setAddressInput] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [jobFields, setJobFields] = useState(
    () => JSON.parse(localStorage.getItem("jobFields")) || []
  );
  const [entranceMode, setEntranceMode] = useState(false);
  const [selectedFieldIndexForEntrance, setSelectedFieldIndexForEntrance] = useState(0);
  const [landUseDetails, setLandUseDetails] = useState(initialLandUseDetails);
  
  const [isPosting, setIsPosting] = useState(false);

  const routeLocation = useLocation();
  const focusJobId = routeLocation.state?.focusJobId || null;
  const focusItemId = routeLocation.state?.focusItemId || null;
  const focusItemType = routeLocation.state?.focusItemType || null;
  const farmFieldDraft = routeLocation.state?.farmFieldDraft || null;
  const linkedFarmFieldId = routeLocation.state?.linkedFarmFieldId || farmFieldDraft?.id || "";

  const isFocusedBrowseMode = Boolean(
    focusJobId || (focusItemId && focusItemType === "dayJob")
  );

  const [mode, setMode] = useState(
    isFocusedBrowseMode ? "browse" : "browse"
  );
  // modes: "browse" | "field" | "day"

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    localStorage.setItem("jobFields", JSON.stringify(jobFields));
  }, [jobFields]);

  useEffect(() => {
    if (appUserLocation?.lat && appUserLocation?.lng) {
      setUserLocation(appUserLocation);
    }
  }, [appUserLocation?.lat, appUserLocation?.lng, appUserLocation?.locationSource, appUserLocation?.source]);

  useEffect(() => {
    if (isFocusedBrowseMode) {
      setMode("browse");
    }
  }, [isFocusedBrowseMode, focusJobId, focusItemId, focusItemType]);

  useEffect(() => {
    if (routeLocation.state?.postType === "land_use") {
      setMode("land_use");
    }
  }, [routeLocation.state?.postType]);

  useEffect(() => {
    if (mode !== "field") return undefined;

    startAppFlow("job_post_field", "job_post_started", {
      feature: "jobs",
      action: "post_field_job",
      abandonEventType: "job_post_abandoned",
    });

    return () => abandonAppFlow("job_post_field");
  }, [mode]);

  useEffect(() => {
    if (!farmFieldDraft) return;

    setMode("field");
    setCropType(farmFieldDraft.grassOrCropType || farmFieldDraft.useType || farmFieldDraft.currentUse || "");
    setAddressInput(farmFieldDraft.locationText || farmFieldDraft.gateLocation || "");

    const savedBoundary =
      Array.isArray(farmFieldDraft.points) ? farmFieldDraft.points :
      Array.isArray(farmFieldDraft.polygonPath) ? farmFieldDraft.polygonPath :
      Array.isArray(farmFieldDraft.boundary) ? farmFieldDraft.boundary :
      Array.isArray(farmFieldDraft.polygon) ? farmFieldDraft.polygon :
      [];

    if (savedBoundary.length >= 3) {
      setJobFields((current) =>
        current.length > 0
          ? current
          : [
              {
                id: `saved-${farmFieldDraft.id || Date.now()}`,
                name: farmFieldDraft.fieldName || "Saved field",
                acres: Number(farmFieldDraft.acres || farmFieldDraft.acreage || 0),
                points: savedBoundary,
                polygonPath: savedBoundary,
                entranceNote: farmFieldDraft.gateLocation || "",
                entrance: farmFieldDraft.entrance || null,
                city: farmFieldDraft.city || "",
                state: farmFieldDraft.state || "",
              },
            ]
      );
    }
  }, [farmFieldDraft]);

  

  const getLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported on this device.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const latVal = pos.coords.latitude;
        const lngVal = pos.coords.longitude;

        setUserLocation({
          lat: latVal,
          lng: lngVal,
          locationSource: LOCATION_SOURCES.BROWSER_GPS,
          source: LOCATION_SOURCES.BROWSER_GPS,
        });

        const location = await reverseGeocode(latVal, lngVal);

        if (location?.city || location?.state) {
          setAddressInput(
            [location.city, location.state].filter(Boolean).join(", ")
          );
        }

        alert("Location loaded ✅");
      },
      (error) => {
        console.error("GPS error:", error);
        alert("Could not get GPS location ❌");
      }
    );
  };

  const handleRemoveField = (fieldId) => {
    setJobFields((prev) => prev.filter((field) => field.id !== fieldId));
  };

  const totalAcres = useMemo(
    () =>
      jobFields
        .reduce((sum, field) => sum + (Number(field.acres) || 0), 0)
        .toFixed(2),
    [jobFields]
  );

  const totalPoints = useMemo(
    () =>
      jobFields.reduce(
        (sum, field) =>
          sum + (field.points?.length || field.polygonPath?.length || 0),
        0
      ),
    [jobFields]
  );

  const locationLabel = useMemo(() => {
    if (addressInput) return addressInput;

    const firstFieldWithLocation = jobFields.find(
      (field) => field.city || field.state
    );

    if (!firstFieldWithLocation) return "No location selected yet";

    return [firstFieldWithLocation.city, firstFieldWithLocation.state]
      .filter(Boolean)
      .join(", ");
  }, [addressInput, jobFields]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("You must be logged in to post a job.");
      return;
    }

    if (!requireVerifiedEmail(auth.currentUser, userProfile)) return;

    if (!requirePremiumPosting()) {
    return;
  }

    if (jobFields.length === 0) {
      alert("Draw at least one field boundary first.");
      return;
    }

const jobTypeLabel = getFieldJobTypeLabel(jobType, customJobType);
const cleanCropType = cropType.trim();

if (jobType === "custom" && !customJobType.trim()) {
  alert("Please enter a custom field job type.");
  return;
}

const confirmed = window.confirm(
  linkedFarmFieldId
    ? "Post this field job using the saved field details? Review the boundary and notes first. This will not alter your saved Farm Records field."
    : "Post this field job now?"
);

if (!confirmed) return;

    try {
      setIsPosting(true);

      const fieldJobRef = await addDoc(collection(db, "fields"), {
        ownerId: auth.currentUser.uid,

jobType,
  jobTypeLabel,
  customJobType: jobType === "custom" ? customJobType.trim() : "",
  cropType: cleanCropType,

        grassType: jobTypeLabel,
        preferredDeal: dealType,
        fields: jobFields,
        totalAcres: Number(totalAcres),
        linkedFarmFieldId: linkedFarmFieldId || "",
        status: "open",
        postedDate: serverTimestamp(),
      });

      if (linkedFarmFieldId) {
        await updateDoc(doc(db, "farmFieldRecords", linkedFarmFieldId), {
          linkedFieldJobId: fieldJobRef.id,
          updatedAt: serverTimestamp(),
        });
      }

      completeAppFlow("job_post_field", "job_post_completed", {
        feature: "jobs",
        action: "post_field_job",
        relatedId: fieldJobRef.id,
      });

      
      setJobType("grass_cutting");
      setCustomJobType("");
      setCropType("");
      setDealType("per_acre");
      setAddressInput("");
      setUserLocation(null);
      setJobFields([]);
      localStorage.removeItem("jobFields");

    alert("Job posted with multiple fields.");
      setMode("browse");
    } catch (error) {
      console.error("Error posting job:", error);
      alert("Failed to post job ❌");
    } finally {
      setIsPosting(false);
    }
  };

  const handleLandUseChange = (event) => {
    const { name, value } = event.target;
    setLandUseDetails((current) => ({ ...current, [name]: value }));
  };

  const handleLandUseSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert("You must be logged in to post land use.");
      return;
    }

    if (!requireVerifiedEmail(auth.currentUser, userProfile)) return;

    if (!landUseDetails.title.trim() || !landUseDetails.description.trim()) {
      alert("Add a title and description for your Land Share post.");
      return;
    }

    try {
      setIsPosting(true);

      const firstField = jobFields[0] || {};
      const firstEntrance = firstField.entrance || {};
      const locationParts = [
        firstField.city,
        firstField.state,
      ].filter(Boolean);
      const finalLocationText =
        addressInput ||
        locationParts.join(", ") ||
        "Location available on map";
      const lat =
        typeof firstField.lat === "number"
          ? firstField.lat
          : typeof userLocation?.lat === "number"
          ? userLocation.lat
          : null;
      const lng =
        typeof firstField.lng === "number"
          ? firstField.lng
          : typeof userLocation?.lng === "number"
          ? userLocation.lng
          : null;
      const acreageValue = landUseDetails.acreage.trim() || totalAcres;
      const user = auth.currentUser;

      await addDoc(collection(db, "communityPosts"), {
        ownerId: user.uid,
        authorId: user.uid,
        authorName: userProfile?.name || user.displayName || user.email || "Community member",
        authorPhoto: userProfile?.profilePicture || user.photoURL || "",
        authorRoles: Array.isArray(userProfile?.roles)
          ? userProfile.roles
          : userProfile?.type
          ? [userProfile.type]
          : [],
        authorRating: Number(userProfile?.rating || 0),
        authorReviewCount: Number(userProfile?.reviewCount || 0),

        type: "land_use",
        category: "land_use",
        title: landUseDetails.title.trim(),
        text: landUseDetails.description.trim(),
        description: landUseDetails.description.trim(),
        landUseType: landUseDetails.landUseType,
        offeringOrSeeking: landUseDetails.offeringOrSeeking,
        acreage: acreageValue,
        fenced: normalizeUnknownBoolean(landUseDetails.fenced),
        waterAccess: normalizeUnknownBoolean(landUseDetails.waterAccess),
        animalUseNotes: landUseDetails.animalUseNotes.trim(),
        animalAllowedNotes: landUseDetails.animalUseNotes.trim(),
        priceType: landUseDetails.priceType,
        price: landUseDetails.price.trim(),

        fields: jobFields,
        polygons: jobFields,
        totalAcres: Number(totalAcres),
        entrance:
          firstEntrance.lat && firstEntrance.lng
            ? {
                lat: firstEntrance.lat,
                lng: firstEntrance.lng,
                note: firstEntrance.note || firstField.entranceNote || "",
              }
            : null,

        city: firstField.city || "",
        state: firstField.state || "",
        county: "",
        locationText: finalLocationText,
        locationNote: finalLocationText,
        lat,
        lng,
        locationMissing: lat === null || lng === null,
        visibilityRadiusMiles: 100,
        likeCount: 0,
        commentCount: 0,
        helpfulCount: 0,
        imageUrl: "",
        imagePath: "",
        hasImage: false,
        isArchived: false,
        isHidden: false,
        status: "active",
        pinned: false,
        isFounderPost: false,
        displayAsBrand: false,
        notify: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setLandUseDetails(initialLandUseDetails);
      setAddressInput("");
      setUserLocation(null);
      setJobFields([]);
      localStorage.removeItem("jobFields");
      alert("Land Share post shared with map area.");
      setMode("browse");
    } catch (error) {
      console.error("Error posting land use:", error);
      alert("Failed to post Land Share listing.");
    } finally {
      setIsPosting(false);
    }
  };

  const requirePremiumPosting = () => {
  if (premiumAllowed) return true;

  alert(
    "Premium is required to post field jobs or day jobs. You can still browse and buy local products for free."
  );

  return false;
};

  const renderModeSwitcher = () => (
    <section className="map-mode-card" aria-label="Map mode">
      <div className="map-mode-toolbar">
        {MAP_MODES.map((item) => {
          const isActive = mode === item.value;

          return (
            <button
              key={item.value}
              type="button"
              data-map-mode={item.value}
              className={`map-mode-button${isActive ? " is-active" : ""}`}
              aria-pressed={isActive}
              onClick={() => {
                if ((item.value === "field" || item.value === "day") && !requirePremiumPosting()) {
                  return;
                }
                setMode(item.value);
              }}
            >
              <span className="map-mode-icon" aria-hidden="true">
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
      <p className="map-mode-helper">{MAP_MODE_HELPERS[mode]}</p>
    </section>
  );

  if (mode === "browse") {
    return (
      <div className="card page-shell page-shell-wide">
        <div className="page-topbar">
          <div>
            <h2>MyFarmingPal Map</h2>
            <p className="page-subtitle">
              
            </p>
          </div>
        </div>

        {renderModeSwitcher()}

        <div className="info-bar">
          <div className="info-panel-title">Browse mode</div>
          <div className="info-panel-text">
            Explore nearby jobs, fields, and land posts.
            {!premiumAllowed && (
  <div style={{ marginTop: "10px" }}>
    <button
      type="button"
      className="btn-primary"
      onClick={() => window.location.assign("/subscribe")}
    >
      Upgrade to post jobs
    </button>
  </div>
)}
          </div>
        </div>
             { /*Browse mapview*/}
        <div className="map-section map-section-hero">
          <MapView
  isLoaded={isLoaded}
  loadError={loadError}
  userLocation={userLocation}
  setUserLocation={setUserLocation}
  completedFields={[]}
  setCompletedFields={() => {}}
  showExistingJobs={true}
  focusJobId={null}
  currentUser={auth.currentUser} 
  showDrawingControls={false}
  entranceMode={entranceMode}
  setEntranceMode={setEntranceMode}
  selectedFieldIndexForEntrance={selectedFieldIndexForEntrance}
/>
        </div>
      </div>
    );
  }

  if (mode === "day") {
    return (
      <div className="card page-shell page-shell-wide">
        <div className="page-topbar">
          <div>
            <h2>MyFarmingPal Map</h2>
            
            <p className="page-subtitle">
              
            </p>
          </div>
        </div>

        {renderModeSwitcher()}

        <div className="info-bar">
          <div className="info-panel-title">Day job mode</div>
          <div className="info-panel-text">
            Choose a location and post local work.
          </div>
        </div>

        <DayJobForm
           userLocation={userLocation}
           isLoaded={isLoaded}
           loadError={loadError}
           userProfile={userProfile}
        />
      </div>
    );
  }

  const isLandUseMode = mode === "land_use";
  const drawingNoun = isLandUseMode ? "Land areas" : "Fields";
  const drawingInfoTitle = isLandUseMode ? "Show your land on the map" : "Field job mode";
  const drawingInfoText = isLandUseMode
    ? "Show available land or find space nearby."
    : "Draw the field boundary and add job details.";

  return (
    <form
      onSubmit={isLandUseMode ? handleLandUseSubmit : handleSubmit}
      className={`card page-shell page-shell-wide${isLandUseMode ? " land-use-posting-flow" : ""}`}
    >
      <div className="page-topbar">
        <div>
          <h2>MyFarmingPal Map</h2>
          <p className="page-subtitle">
            
          </p>
        </div>

        <div className="map-stat-pills">
          <span className="map-stat-pill">
            {drawingNoun}: <strong>{jobFields.length}</strong>
          </span>
          <span className="map-stat-pill">
            Acres: <strong>{totalAcres}</strong>
          </span>
          <span className="map-stat-pill">
            Points: <strong>{totalPoints}</strong>
          </span>
        </div>
      </div>

      <div className="context-tip">
        <strong>Tip:</strong> For field jobs, describe the work, timing,
        equipment expectations, and general location clearly. You can keep
        exact details for messages.
      </div>

      {renderModeSwitcher()}

      <div className="info-bar">
        <div className="info-panel-title">{drawingInfoTitle}</div>
        <div className="info-panel-text">
          {drawingInfoText}
        </div>
      </div>

      <div className="posting-layout">
        <div className="posting-main">
          <div className="toolbar-row posting-toolbar">
            <div style={{ flex: 1, minWidth: "240px" }}>
              <AddressAutocomplete
                isLoaded={isLoaded}
                loadError={loadError}
                onPlaceSelect={({ lat, lng, formattedAddress }) => {
                  setUserLocation({ lat, lng });
                  setAddressInput(formattedAddress || "");
                }}
              />
            </div>

            <button type="button" onClick={getLocation}>
              Use My Location
            </button>
          </div>
              
<div className="mode-label">
  {mode === "browse" && "Browsing"}
  {mode === "field" && "Drawing Fields"}
  {mode === "day" && "Posting Day Job"}
  {mode === "land_use" && "Drawing Land Share"}
</div>
            {/* Post Field */}
          <div className="map-section">
            <MapView
  isLoaded={isLoaded}
  loadError={loadError}
  userLocation={userLocation}
  setUserLocation={setUserLocation}
  completedFields={jobFields}
  setCompletedFields={setJobFields}
  showExistingJobs={false}
  focusJobId={null}
  currentUser={auth.currentUser}
  showDrawingControls={true}
  entranceMode={entranceMode}
  setEntranceMode={setEntranceMode}
  selectedFieldIndexForEntrance={selectedFieldIndexForEntrance}
  setSelectedFieldIndexForEntrance={setSelectedFieldIndexForEntrance}
  drawingContext={isLandUseMode ? "land_use" : "field_job"}
/>
          </div>
        </div>

        <aside className="posting-sidebar">
          <div className="summary-box summary-box-sticky">
            <div className="summary-title">{isLandUseMode ? "Land Share Summary" : "Job Summary"}</div>

            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Location</span>
                <strong>{locationLabel}</strong>
              </div>

              <div className="summary-item">
                <span className="summary-label">{isLandUseMode ? "Land areas" : "Fields"}</span>
                <strong>{jobFields.length}</strong>
              </div>

              <div className="summary-item">
                <span className="summary-label">Total Acres</span>
                <strong>{totalAcres}</strong>
              </div>

              <div className="summary-item">
  <span className="summary-label">{isLandUseMode ? "Land Use Type" : "Job Type"}</span>
  <strong>
    {isLandUseMode
      ? LAND_USE_TYPES.find((type) => type.value === landUseDetails.landUseType)?.label || "Land Use"
      : getFieldJobTypeLabel(jobType, customJobType)}
  </strong>
</div>

{isLandUseMode && (
  <>
    <div className="summary-item">
      <span className="summary-label">Offering / Seeking</span>
      <strong>{landUseDetails.offeringOrSeeking === "seeking" ? "Seeking" : "Offering"}</strong>
    </div>
    <div className="summary-item">
      <span className="summary-label">Fenced</span>
      <strong>{landUseDetails.fenced === "true" ? "Yes" : landUseDetails.fenced === "false" ? "No" : "Unknown"}</strong>
    </div>
    <div className="summary-item">
      <span className="summary-label">Water Access</span>
      <strong>{landUseDetails.waterAccess === "true" ? "Yes" : landUseDetails.waterAccess === "false" ? "No" : "Unknown"}</strong>
    </div>
    <div className="summary-item">
      <span className="summary-label">Price Type</span>
      <strong>{landUseDetails.priceType}</strong>
    </div>
  </>
)}

{!isLandUseMode && cropType.trim() && (
  <div className="summary-item">
    <span className="summary-label">Crop / Field Type</span>
    <strong>{cropType}</strong>
  </div>
)}

              
            </div>
          </div>

          <div className="section form-panel">
            <h3>{isLandUseMode ? "Land Share Details" : "Job Details"}</h3>

            <div className="form-stack">
              {isLandUseMode ? (
                <>
                  <div>
                    <label className="form-label" htmlFor="land-use-title">
                      Title
                    </label>
                    <input
                      id="land-use-title"
                      name="title"
                      value={landUseDetails.title}
                      onChange={handleLandUseChange}
                      placeholder="Horse pasture available near Shelbyville"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-type">
                      Land Use Type
                    </label>
                    <select
                      id="land-use-type"
                      name="landUseType"
                      value={landUseDetails.landUseType}
                      onChange={handleLandUseChange}
                      required
                    >
                      {LAND_USE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-direction">
                      Offering or Seeking
                    </label>
                    <select
                      id="land-use-direction"
                      name="offeringOrSeeking"
                      value={landUseDetails.offeringOrSeeking}
                      onChange={handleLandUseChange}
                    >
                      <option value="offering">Offering</option>
                      <option value="seeking">Seeking</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-acreage">
                      Acreage optional
                    </label>
                    <input
                      id="land-use-acreage"
                      name="acreage"
                      value={landUseDetails.acreage}
                      onChange={handleLandUseChange}
                      placeholder={`${totalAcres} acres from map`}
                    />
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-fenced">
                      Fenced
                    </label>
                    <select
                      id="land-use-fenced"
                      name="fenced"
                      value={landUseDetails.fenced}
                      onChange={handleLandUseChange}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-water">
                      Water Access
                    </label>
                    <select
                      id="land-use-water"
                      name="waterAccess"
                      value={landUseDetails.waterAccess}
                      onChange={handleLandUseChange}
                    >
                      <option value="unknown">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-price-type">
                      Price Type
                    </label>
                    <select
                      id="land-use-price-type"
                      name="priceType"
                      value={landUseDetails.priceType}
                      onChange={handleLandUseChange}
                    >
                      <option value="free">Free</option>
                      <option value="paid">Paid</option>
                      <option value="trade">Trade</option>
                      <option value="contact">Contact</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label" htmlFor="land-use-price">
                      Price optional
                    </label>
                    <input
                      id="land-use-price"
                      name="price"
                      value={landUseDetails.price}
                      onChange={handleLandUseChange}
                      placeholder="$50/month, trade, contact..."
                    />
                  </div>

                  <div className="land-use-form-wide">
                    <label className="form-label" htmlFor="land-use-description">
                      Description
                    </label>
                    <textarea
                      id="land-use-description"
                      name="description"
                      value={landUseDetails.description}
                      onChange={handleLandUseChange}
                      rows={4}
                      placeholder="Describe access, rules, timing, animal limits, parking, gates, water, and anything nearby people should know."
                      required
                    />
                  </div>

                  <div className="land-use-form-wide">
                    <label className="form-label" htmlFor="land-use-notes">
                      Animal/use notes optional
                    </label>
                    <textarea
                      id="land-use-notes"
                      name="animalUseNotes"
                      value={landUseDetails.animalUseNotes}
                      onChange={handleLandUseChange}
                      rows={3}
                      placeholder="Horses only, garden use only, no intact males, temporary holding..."
                    />
                  </div>
                </>
              ) : (
                <>
              <div>
  <label className="form-label" htmlFor="field-job-type">
    Field Job Type
  </label>

  <select
    id="field-job-type"
    name="fieldJobType"
    value={jobType}
    onChange={(e) => setJobType(e.target.value)}
    required
  >
    {FIELD_JOB_TYPES.map((type) => (
      <option key={type.value} value={type.value}>
        {type.label}
      </option>
    ))}
  </select>
</div>

{jobType === "custom" && (
  <div>
    <label className="form-label" htmlFor="custom-field-job-type">
      Custom Job Type
    </label>

    <input
      id="custom-field-job-type"
      name="customFieldJobType"
      type="text"
      value={customJobType}
      onChange={(e) => setCustomJobType(e.target.value)}
      placeholder="Example: orchard mowing, pasture cleanup"
      required
    />
  </div>
)}

<div>
  <label className="form-label" htmlFor="field-crop-type">
    Crop / Field Type
  </label>

  <input
    id="field-crop-type"
    name="fieldCropType"
    type="text"
    value={cropType}
    onChange={(e) => setCropType(e.target.value)}
    placeholder="Example: Bermuda, corn, soybeans, hay field"
  />
</div>

              
                </>
              )}
            </div>
          </div>

         {jobFields[selectedFieldIndexForEntrance]?.entrance?.lat && (
  <div className="section form-panel entrance-tool-card">
    <h3>Entrance Note</h3>

    {jobFields.length > 1 && (
      <div>
        <label className="form-label">Field</label>
        <select
          value={selectedFieldIndexForEntrance}
          onChange={(e) =>
            setSelectedFieldIndexForEntrance(Number(e.target.value))
          }
        >
          {jobFields.map((field, index) => (
            <option key={field.id || index} value={index}>
              Field #{index + 1}
            </option>
          ))}
        </select>
      </div>
    )}

    <input
      value={
        jobFields[selectedFieldIndexForEntrance]?.entrance?.note ||
        jobFields[selectedFieldIndexForEntrance]?.entranceNote ||
        ""
      }
      onChange={(e) => {
        const note = e.target.value;

        setJobFields((prev) =>
          prev.map((field, index) =>
            index === selectedFieldIndexForEntrance
              ? {
                  ...field,
                  entrance: {
                    ...(field.entrance || {}),
                    note,
                  },
                  entranceNote: note,
                }
              : field
          )
        );
      }}
      placeholder="Example: Green gate beside mailbox, trailer-friendly"
    />
  </div>
)}

        </aside>
      </div>

      <div className="section">
        <div className="section-heading-row">
          <h3>{isLandUseMode ? "Land Areas in this Post" : "Fields in this Job"}</h3>
          {jobFields.length > 0 && (
            <div className="section-heading-note">
              {isLandUseMode ? "Review each land area before posting" : "Review each field before posting"}
            </div>
          )}
        </div>

        {jobFields.length === 0 ? (
          <div className="empty-state">
            {isLandUseMode
              ? "No land area added yet. Draw the pasture, garden plot, fenced area, or field access area so people can see what is available."
              : "No fields added yet. Draw a boundary on the map to get started."}
          </div>
        ) : (
          <>
            <div className="field-list">
              {jobFields.map((field, index) => (
                <div
                  key={field.id || index}
                  className="field-card"
                  onClick={() => window.zoomToField?.(field)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="field-card-header">
                    <strong className="field-card-title">
                      {isLandUseMode ? `Land Area #${index + 1}` : `Field #${index + 1}`}
                    </strong>

                    <span className="acre-badge">
                      {Number(field.acres || 0).toFixed(2)} acres
                    </span>
                  </div>

                  <div className="field-card-meta">
                    {(field.points?.length || field.polygonPath?.length || 0)} boundary points
                    {field.category ? ` • ${field.category}` : ""}
                    {field.city || field.state
                      ? ` • ${field.city || ""}${field.city && field.state ? ", " : ""}${field.state || ""}`
                      : ""}
                  
                  {(field.entrance?.lat || field.entranceLat) &&
  (field.entrance?.lng || field.entranceLng) && (
    <div className="field-entrance-preview">
      <strong>Entrance marked</strong>

      {(field.entrance?.note || field.entranceNote) && (
        <span>{field.entrance?.note || field.entranceNote}</span>
      )}
    </div>
  )}
                  
                  </div>

                  <div className="field-card-actions">
                    <button
                      type="button"
                      className="btn-soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.zoomToField?.(field);
                      }}
                    >
                      Zoom
                    </button>

                    <button
                      type="button"
                      className="btn-danger-soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveField(field.id);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="summary-box posting-footer-box">
              <div className="posting-footer-box-left">
                <strong>{jobFields.length}</strong> fields -{" "}
                <strong>{totalAcres}</strong> acres total
              </div>

              {isLandUseMode && (
                <button type="submit" className="btn-primary" disabled={isPosting}>
                  {isPosting ? "Posting..." : "Post Land Share"}
                </button>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={isPosting}
                style={{ display: isLandUseMode ? "none" : undefined }}
              >
                {isPosting ? "Posting..." : "Post Job"}
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default FieldListingForm;
